import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import backgroundImages from "../data/backgroundImages";
import SEO from "../components/SEO";
import {
  organizationSchema,
  localBusinessSchema,
  websiteSchema,
} from "../utils/schemaMarkup";

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const stats = [
    { number: "30+", label: "Years Experience", delay: "0.2s" },
    { number: "500+", label: "Happy Clients", delay: "0.4s" },
    { number: "1000+", label: "Products", delay: "0.6s" },
    { number: "24/7", label: "Support", delay: "0.8s" },
  ];

  const testimonials = [
    {
      text: "Exceptional quality and reliable service. Ceekay Enterprise has been our trusted partner for industrial chemicals.",
      author: "Manufacturing Director",
      company: "Devanshi Corporation",
    },
    {
      text: "Outstanding logistics and timely delivery. Their expertise in chemical imports is unmatched.",
      author: "Supply Chain Manager",
      company: "Milton Chemicals",
    },
    {
      text: "Quality products and professional service. Highly recommend for all industrial chemical needs.",
      author: "Operations Head",
      company: "Devanshi Corporation",
    },
  ];

  useEffect(() => {
    setIsVisible(true);

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
      setCurrentImageIndex(
        (prevIndex) => (prevIndex + 1) % backgroundImages.length
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length, backgroundImages.length]);

  const homeSchema = {
    "@context": "https://schema.org",
    "@graph": [organizationSchema, localBusinessSchema, websiteSchema],
  };

  return (
    <div className="page active">
      <SEO
        title="Ceekay Enterprise - Leading Industrial Chemical Importer & Supplier in India | Since 1992"
        description="Ceekay Enterprise - Your trusted partner for industrial chemicals in India since 1992. Importing high-quality borates, sulfates, nitrates, oxides, and specialty chemicals. Serving construction, textiles, agriculture, manufacturing, and water treatment industries with competitive pricing and reliable delivery across India."
        keywords="industrial chemical suppliers India, chemical importer Mumbai, borates supplier India, sodium tetraborate pentahydrate, boric acid importer, calcium chloride supplier, industrial chemicals distributor, buy chemicals online India, Ceekay Enterprise"
        canonical="https://www.ceekayenterprise.com/"
        schema={homeSchema}
      />
      {/* Hero Section */}
      <div className="modern-hero">
        <div className="hero-background">
          {/* Background Images with Transitions */}
          {backgroundImages.map((image, index) => (
            <div
              key={index}
              className={`background-image ${
                index === currentImageIndex ? "active" : ""
              }`}
              style={{ backgroundImage: `url(${image})` }}
            />
          ))}

          {/* Dark Overlay */}
          <div className="background-overlay" />

          {/* Floating Particles */}
          <div className="floating-particles">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 20}s`,
                  animationDuration: `${15 + Math.random() * 10}s`,
                }}
              />
            ))}
          </div>
        </div>

        <div className="hero-content">
          <div className={`hero-text ${isVisible ? "animate-in" : ""}`}>
            <div className="company-badge">
              <span className="badge-icon">⚡</span>
              <span>Est. 1992 • Trusted Excellence</span>
            </div>

            <h1 className="hero-title">
              <span className="title-line">Welcome to</span>
              <span className="title-main">Ceekay Enterprise&nbsp;</span>
              <span className="title-accent">Industrial Chemicals</span>
            </h1>

            <p className="hero-description">
              Your premier partner in importing high-grade industrial chemicals.
              Delivering excellence across India with unmatched quality and
              reliability.
            </p>

            <div className="hero-actions">
              <Link to="/products">
                <button className="primary-cta">
                  <span>Explore Products</span>
                  <div className="cta-arrow">→</div>
                </button>
              </Link>
              <Link to="/about">
                <button className="secondary-cta">
                  <span>Learn More</span>
                </button>
              </Link>
            </div>
          </div>

          <div className="hero-visual">
            <div className="visual-container">
              <div className="visual-card card-1">
                <div className="card-icon">🏭</div>
                <div className="card-text">Industrial Grade</div>
              </div>
              <div className="visual-card card-2">
                <div className="card-icon">🔬</div>
                <div className="card-text">Quality Tested</div>
              </div>
              <div className="visual-card card-3">
                <div className="card-icon">🚚</div>
                <div className="card-text">Fast Delivery</div>
              </div>
            </div>
          </div>
        </div>

        {/* Image Navigation Dots */}
        <div className="image-navigation">
          {backgroundImages.map((_, index) => (
            <button
              key={index}
              className={`nav-dot ${
                index === currentImageIndex ? "active" : ""
              }`}
              onClick={() => setCurrentImageIndex(index)}
            />
          ))}
        </div>
      </div>

      {/* Stats Section */}
      <div className="stats-section">
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`stat-item ${isVisible ? "animate-up" : ""}`}
              style={{ animationDelay: stat.delay }}>
              <div className="stat-number">{stat.number}</div>
              <div className="stat-label">{stat.label}</div>
              <div className="stat-glow"></div>
            </div>
          ))}
        </div>
      </div>

      {/* Features Section */}
      <div className="modern-features">
        <div className="section-header">
          <h2 className="section-title">Why Choose Us</h2>
          <p className="section-subtitle">
            Excellence in every aspect of our service
          </p>
        </div>

        <div className="features-grid">
          <div className="feature-card modern-card">
            <div className="card-header">
              <div className="feature-icon-modern">
                <div className="icon-bg">🏭</div>
              </div>
              <h3>Industrial Excellence</h3>
            </div>
            <p>
              Leading importer and supplier of high-quality industrial chemicals
              and specialty products for diverse industries worldwide.
            </p>
            <div className="card-footer">
              <span className="learn-more">Learn More →</span>
            </div>
          </div>

          <div className="feature-card modern-card">
            <div className="card-header">
              <div className="feature-icon-modern">
                <div className="icon-bg">🚚</div>
              </div>
              <h3>Reliable Supply Chain</h3>
            </div>
            <p>
              Efficient logistics and distribution network ensuring timely
              delivery across India with multiple shipping options and real-time
              tracking.
            </p>
            <div className="card-footer">
              <span className="learn-more">Learn More →</span>
            </div>
          </div>

          <div className="feature-card modern-card">
            <div className="card-header">
              <div className="feature-icon-modern">
                <div className="icon-bg">✅</div>
              </div>
              <h3>Quality Assurance</h3>
            </div>
            <p>
              Verified company with stringent quality control measures and
              certified products meeting international industry standards and
              regulations.
            </p>
            <div className="card-footer">
              <span className="learn-more">Learn More →</span>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="testimonials-section">
        <div className="section-header">
          <h2 className="section-title" style={{ color: "gray" }}>
            What Our Clients Say
          </h2>
        </div>

        <div className="testimonial-carousel">
          <div className="testimonial-container">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className={`testimonial-slide ${
                  index === currentSlide ? "active" : ""
                }`}>
                <div className="testimonial-content">
                  <div className="quote-icon">"</div>
                  <p className="testimonial-text">{testimonial.text}</p>
                  <div className="testimonial-author">
                    <div className="author-info">
                      <div className="author-name">{testimonial.author}</div>
                      <div className="author-company">
                        {testimonial.company}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="carousel-indicators">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`indicator ${
                  index === currentSlide ? "active" : ""
                }`}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="cta-section">
        <div className="cta-content">
          <h2>Ready to Get Started?</h2>
          <p>
            Join hundreds of satisfied clients who trust us for their industrial
            chemical needs.
          </p>
          <div className="cta-actions">
            <Link to="/contact" className="primary-cta">
              <span>Contact Us Today</span>
              <div className="cta-arrow">→</div>
            </Link>
            <Link to="/products" className="secondary-cta">
              <span>View Products</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
